import React, { useState } from 'react';

import { useSnackbar } from 'notistack';
import { Control, UseFormSetValue, useWatch } from 'react-hook-form';

import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';

import { TourOfferConfiguration } from '~/types/tours/tourOffer';

import { NativeTourForm } from './nativeTourForm';

interface Props {
  control: Control<NativeTourForm>;
  setValue: UseFormSetValue<NativeTourForm>;
  target: TourOfferConfiguration;
  configurations: Array<TourOfferConfiguration>;
  /** A Salesforce tour's itinerary has its own tab, so it can be copied on its own. */
  isSalesforceTour: boolean;
}

type Part = 'content' | 'itinerary' | 'packages';

const PART_LABELS: Record<Part, string> = {
  content: 'About, highlights and inclusions',
  itinerary: 'Itinerary',
  packages: 'Package inclusions (to packages with the same name)',
};

const withFreshIds = <T extends { id: string }>(entries: Array<T>): Array<T> =>
  entries.map((entry) => ({ ...entry, id: crypto.randomUUID() }));

/** Copies parts of a sibling option into this one, in the form. Nothing is saved until the page is. */
export default function NativeTourCopyFromOption({
  control,
  setValue,
  target,
  configurations,
  isSalesforceTour,
}: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const form = useWatch({ control }) as NativeTourForm;
  const [isOpen, setIsOpen] = useState(false);
  const siblings = configurations.filter((configuration) => configuration.id !== target.id);
  const [sourceId, setSourceId] = useState<string>('');
  const parts: Array<Part> = isSalesforceTour ? ['content', 'itinerary', 'packages'] : ['content', 'packages'];
  const [chosen, setChosen] = useState<Array<Part>>(parts);

  if (siblings.length === 0) {
    return null;
  }

  const copy = () => {
    const source = configurations.find((configuration) => configuration.id === sourceId);
    if (!source) {
      return;
    }
    const options = { shouldDirty: true };
    if (chosen.includes('content')) {
      const content = form.configurations[source.id];
      setValue(
        `configurations.${target.id}`,
        {
          ...form.configurations[target.id],
          description: content.description,
          inclusions: withFreshIds(content.inclusions),
        },
        options,
      );
    }
    if (chosen.includes('itinerary') && form.optionItineraries[source.id]) {
      setValue(`optionItineraries.${target.id}`, form.optionItineraries[source.id], options);
    }
    if (chosen.includes('packages')) {
      const nameOf = (configuration: TourOfferConfiguration, id: string) =>
        configuration.packages.find((pkg) => pkg.id === id)?.name;
      const sourcePackages = form.optionDetails[source.id].packages;
      setValue(
        `optionDetails.${target.id}.packages`,
        form.optionDetails[target.id].packages.map((pkg) => {
          const match = sourcePackages.find((other) => nameOf(source, other.id) === nameOf(target, pkg.id));
          return match ? { ...pkg, inclusions: withFreshIds(match.inclusions) } : pkg;
        }),
        options,
      );
    }
    enqueueSnackbar(`Copied from ${source.name}. Check it, then save.`, { variant: 'info' });
    setIsOpen(false);
  };

  return (
    <>
      <Button variant="text" startIcon={<ContentCopyIcon />} onClick={() => setIsOpen(true)}>
        Copy from another option
      </Button>
      <Dialog open={isOpen} onClose={() => setIsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Copy into {target.name}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} pt={1}>
            <DialogContentText>
              Replaces what's chosen with the other option's, as it is in this page. Save the page to keep it.
            </DialogContentText>
            <TextField select label="Copy from" value={sourceId} onChange={(event) => setSourceId(event.target.value)}>
              {siblings.map((configuration) => (
                <MenuItem key={configuration.id} value={configuration.id}>
                  {form.configurations[configuration.id]?.title || configuration.name}
                </MenuItem>
              ))}
            </TextField>
            <FormGroup>
              {parts.map((part) => (
                <FormControlLabel
                  key={part}
                  control={
                    <Checkbox
                      checked={chosen.includes(part)}
                      onChange={(event) =>
                        setChosen(event.target.checked ? [...chosen, part] : chosen.filter((other) => other !== part))
                      }
                    />
                  }
                  label={PART_LABELS[part]}
                />
              ))}
            </FormGroup>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setIsOpen(false)}>
            Cancel
          </Button>
          <Button variant="contained" disabled={!sourceId || chosen.length === 0} onClick={copy}>
            Copy
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
